import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import { languageColor } from './dotglobal-language-colors.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TFileMode = 'code' | 'blame' | 'history';
type TInlinePart = string | TemplateResult;

@customElement('dotglobal-file-view')
export class DotglobalFileView extends DeesElement {
  @property({ type: Object })
  public accessor repo: interfaces.IPublicRepo | null = null;

  @property({ type: Object })
  public accessor file: interfaces.IPublicFile | null = null;

  @property({ type: Array })
  public accessor commits: interfaces.IPublicCommit[] = [];

  @state()
  private accessor mode: TFileMode = 'code';

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        color: var(--text-primary, #0a0a0a);
        font-family: var(--font-sans);
      }

      * {
        box-sizing: border-box;
      }

      a {
        color: inherit;
        text-decoration: none;
      }

      button {
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        cursor: pointer;
      }

      dees-icon {
        display: inline-flex;
        flex-shrink: 0;
      }

      .page {
        width: 100%;
        max-width: var(--maxw, 1180px);
        margin: 0 auto;
        padding: 0 24px;
      }

      .row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      }

      .grow {
        flex: 1;
      }

      .card {
        overflow: hidden;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 8px;
        background: var(--bg-primary, #ffffff);
      }

      .filebar {
        min-height: 46px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 14px;
        border-bottom: 1px solid var(--border-default, #e4e4e7);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 13px;
        font-weight: 600;
      }

      .breadcrumbs {
        display: flex;
        align-items: center;
        gap: 5px;
        min-width: 0;
        font-family: var(--font-mono);
        font-size: 14px;
      }

      .breadcrumbs button {
        color: var(--accent, #3b82f6);
      }

      .breadcrumbs button:hover {
        text-decoration: underline;
      }

      .breadcrumbs .last,
      .breadcrumbs .sl {
        color: var(--text-muted, #71717a);
      }

      .seg {
        display: inline-flex;
        padding: 2px;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 6px;
        background: var(--bg-tertiary, #f4f4f5);
      }

      .seg button {
        height: 26px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0 11px;
        border-radius: 4px;
        color: var(--text-secondary, #333333);
        font-size: 12px;
        font-weight: 500;
      }

      .seg button:hover {
        color: var(--text-primary, #0a0a0a);
      }

      .seg button.on {
        color: var(--text-primary, #0a0a0a);
        background: var(--bg-primary, #ffffff);
        box-shadow: var(--shadow-xs, 0 1px 2px 0 rgb(0 0 0 / 0.05));
      }

      .seg button:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .btn {
        height: 30px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 0 11px;
        border: 1px solid var(--border-strong, #cccccc);
        border-radius: 6px;
        color: var(--text-primary, #0a0a0a);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        transition: background 0.15s, border-color 0.15s, color 0.15s;
      }

      .btn:hover {
        background: var(--hover, rgba(0, 0, 0, 0.04));
      }

      .btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .btn.ghost {
        border-color: transparent;
        background: transparent;
        color: var(--text-secondary, #333333);
      }

      .muted {
        color: var(--text-muted, #71717a);
      }

      .sha {
        color: var(--accent, #3b82f6);
        font-family: var(--font-mono);
        font-size: 12px;
      }

      .lang-dot {
        width: 11px;
        height: 11px;
        border-radius: 50%;
        background: var(--accent, #3b82f6);
        flex-shrink: 0;
      }

      .meta-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--text-muted, #71717a);
        font-size: 12px;
      }

      .badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 2px 8px;
        border-radius: 4px;
        border: 1px solid transparent;
        color: var(--text-secondary, #333333);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 11.5px;
        font-weight: 500;
        line-height: 1.5;
        white-space: nowrap;
      }

      .badge.warning {
        color: #92400e;
        background: #fef3c7;
      }

      .codeview {
        width: 100%;
        border-collapse: collapse;
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 20px;
      }

      .codeview td {
        vertical-align: top;
      }

      .codeview .ln {
        width: 1%;
        padding: 0 14px 0 18px;
        border-right: 1px solid var(--border-subtle, #eeeeef);
        color: var(--text-muted, #71717a);
        text-align: right;
        white-space: nowrap;
        user-select: none;
      }

      .codeview .code {
        min-width: 620px;
        padding: 0 16px;
        color: var(--text-primary, #0a0a0a);
        white-space: pre;
      }

      .codeview tr:hover td {
        background: var(--row-hover, rgba(59, 130, 246, 0.06));
      }

      .md {
        color: var(--text-primary, #0a0a0a);
        font-size: 14.5px;
        line-height: 1.7;
        overflow-wrap: anywhere;
      }

      .md > *:first-child {
        margin-top: 0;
      }

      .md h1 {
        margin: 28px 0 14px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border-default, #e4e4e7);
        font-family: var(--font-display);
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.02em;
      }

      .md h2 {
        margin: 26px 0 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid var(--border-subtle, #eeeeef);
        font-family: var(--font-display);
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      .md h3 {
        margin: 22px 0 10px;
        font-size: 16px;
        font-weight: 600;
      }

      .md p {
        margin: 0 0 14px;
      }

      .md a {
        color: var(--accent, #3b82f6);
      }

      .md a:hover {
        text-decoration: underline;
      }

      .md ul,
      .md ol {
        margin: 0 0 14px;
        padding-left: 24px;
      }

      .md li {
        margin: 4px 0;
      }

      .md code {
        padding: 0.15em 0.4em;
        border-radius: 4px;
        background: var(--code-bg, #f4f4f5);
        font-family: var(--font-mono);
        font-size: 12.5px;
      }

      .md pre {
        margin: 0 0 16px;
        padding: 14px 16px;
        overflow: auto;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 6px;
        background: var(--code-bg, #f4f4f5);
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 1.6;
        white-space: pre;
      }

      .md pre code {
        padding: 0;
        background: none;
      }

      .tok-keyword {
        color: #7c3aed;
        font-weight: 600;
      }

      .tok-string {
        color: #15803d;
      }

      .tok-comment {
        color: var(--text-muted, #71717a);
        font-style: italic;
      }

      .tok-number {
        color: #b45309;
      }

      .md blockquote {
        margin: 0 0 14px;
        padding: 2px 16px;
        border-left: 3px solid var(--border-strong, #cccccc);
        color: var(--text-secondary, #333333);
      }

      .md table {
        border-collapse: collapse;
        margin: 0 0 16px;
        font-size: 13.5px;
      }

      .md th,
      .md td {
        border: 1px solid var(--border-default, #e4e4e7);
        padding: 7px 13px;
        text-align: left;
      }

      .md th {
        background: var(--bg-tertiary, #f4f4f5);
        font-weight: 600;
      }

      .md hr {
        margin: 22px 0;
        border: 0;
        border-top: 1px solid var(--border-default, #e4e4e7);
      }

      .listrow {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--border-subtle, #eeeeef);
      }

      .listrow:last-child {
        border-bottom: 0;
      }

      .listrow:hover {
        background: var(--row-hover, rgba(59, 130, 246, 0.06));
      }

      .list-title {
        color: var(--text-primary, #0a0a0a);
        font-size: 13.5px;
        font-weight: 500;
        line-height: 1.4;
      }

      .list-sub {
        margin-top: 3px;
        color: var(--text-muted, #71717a);
        font-size: 12px;
      }

      .avatar {
        position: relative;
        width: 28px;
        height: 28px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        overflow: hidden;
        border-radius: 50%;
        color: var(--accent-fg, #ffffff);
        background: var(--accent, #3b82f6);
        font-size: 12px;
        font-weight: 600;
      }

      .avatar img {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .empty {
        padding: 48px 20px;
        text-align: center;
        color: var(--text-muted, #71717a);
      }

      @media (max-width: 820px) {
        .page {
          padding: 0 16px;
        }

        .filebar {
          align-items: flex-start;
          padding: 10px 14px;
          flex-wrap: wrap;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    if (!this.repo || !this.file) {
      return html`<div class="page"><div class="empty">File not found.</div></div>`;
    }

    const rawContent = this.file.content.replace(/\n$/, '');
    const lines = rawContent ? rawContent.split('\n') : [];
    const languageLabel = this.getFileLanguageLabel(this.file.name);

    return html`
      <div class="page">
        <div class="row" style="justify-content: space-between; gap: 10px; margin-bottom: 14px">
          ${this.renderBreadcrumbs(this.repo, this.file.path)}
          <div class="seg">
            <button class=${this.mode === 'code' ? 'on' : ''} @click=${() => { this.mode = 'code'; }}>${this.icon('Code')} Code</button>
            <button disabled title="Blame data is not exposed through the current public API yet">${this.icon('GitCommit')} Blame</button>
            <button class=${this.mode === 'history' ? 'on' : ''} @click=${() => { this.mode = 'history'; }}>${this.icon('History')} History</button>
          </div>
        </div>
        <div class="card">
          <div class="filebar">
            <span class="row" style="gap: 8px; font-size: 12.5px; font-weight: 400">
              <b class="sha" style="color: var(--text-primary, #0a0a0a)">${lines.length}</b><span class="muted">lines</span>
              <span class="muted">·</span><span class="muted sha">${this.formatBytes(this.file.size)}</span>
              <span class="muted">·</span><span class="meta-item"><span class="lang-dot" style=${`background:${languageColor(languageLabel)}`}></span>${languageLabel}</span>
              ${this.file.truncated ? html`<span class="badge warning">preview truncated</span>` : ''}
              ${this.file.isBinary ? html`<span class="badge warning">binary</span>` : ''}
            </span>
            <span class="grow"></span>
            <button class="btn" ?disabled=${this.file.isBinary} title=${this.file.isBinary ? 'Binary preview text is not copied' : this.file.truncated ? 'Copy preview text' : 'Copy file contents'} @click=${() => { if (!this.file!.isBinary) this.emitCopy(this.file!.content); }}>
              ${this.icon('Copy')} ${this.file.truncated ? 'Copy preview' : 'Copy'}
            </button>
          </div>
          ${this.renderModeContent(this.file, lines)}
        </div>
      </div>
    `;
  }

  private renderModeContent(fileArg: interfaces.IPublicFile, linesArg: string[]): TemplateResult {
    if (this.mode === 'history') {
      return html`
        <div>
          ${this.commits.length
            ? this.commits.map((commit) => this.renderHistoryRow(commit))
            : html`<div class="empty">No commit data available.</div>`}
        </div>
      `;
    }

    if (this.mode === 'blame') {
      return html`
        <div class="empty">
          Blame data is not exposed through the current public API yet.
          <div style="margin-top: 12px">
            <span class="badge warning">source view unavailable here</span>
          </div>
        </div>
      `;
    }

    if (fileArg.truncated || fileArg.isBinary) {
      return html`
        <div class="empty">
          ${fileArg.content}
          <div style="margin-top: 12px">
            <span class="badge warning">source view unavailable here</span>
          </div>
        </div>
      `;
    }

    if (this.isMarkdownFile(fileArg.name)) {
      return html`<div class="md" style="padding: 20px 24px">${this.renderMarkdown(fileArg.content, 500)}</div>`;
    }

    return this.renderCode(fileArg, linesArg);
  }

  private renderCode(fileArg: interfaces.IPublicFile, linesArg: string[]): TemplateResult {
    const visibleLines = linesArg.slice(0, 500);
    const languageLabel = this.getFileLanguageLabel(fileArg.name);
    return html`
      ${fileArg.truncated ? html`<div class="empty">${fileArg.content}</div>` : ''}
      <table class="codeview">
        <tbody>
          ${visibleLines.map((line, index) => html`<tr><td class="ln">${index + 1}</td><td class="code">${line ? this.renderHighlightedCode(line, languageLabel) : ' '}</td></tr>`)}
        </tbody>
      </table>
      ${linesArg.length > visibleLines.length ? html`<div class="empty">Preview limited to first ${visibleLines.length} lines.</div>` : ''}
    `;
  }

  private renderHistoryRow(commitArg: interfaces.IPublicCommit): TemplateResult {
    return html`
      <div class="listrow">
        ${this.renderAvatar(commitArg.authorName, commitArg.authorAvatarUrl)}
        <div style="min-width: 0; flex: 1">
          <div class="list-title">${commitArg.title || commitArg.shortSha}</div>
          <div class="list-sub">${commitArg.authorName} · ${this.timeAgo(commitArg.createdAt)} · ${commitArg.files.length || 1} file${commitArg.files.length === 1 ? '' : 's'} changed</div>
        </div>
        <span class="badge sha">${commitArg.shortSha}</span>
      </div>
    `;
  }

  private renderBreadcrumbs(repoArg: interfaces.IPublicRepo, pathArg: string): TemplateResult {
    const parts = pathArg.split('/').filter(Boolean);
    const directoryParts = parts.slice(0, -1);
    const fileName = parts[parts.length - 1] ?? pathArg;
    return html`
      <div class="breadcrumbs">
        <button @click=${() => this.emitNavigate(this.getRepoHash(repoArg.org, repoArg.name))}>${this.icon('BookOpen')} ${repoArg.name}</button>
        ${directoryParts.map((part, index) => {
          const subPath = directoryParts.slice(0, index + 1).join('/');
          return html`<span class="sl">/</span><button @click=${() => this.emitNavigate(this.getRepoHash(repoArg.org, repoArg.name, subPath))}>${part}</button>`;
        })}
        <span class="sl">/</span><span class="last">${fileName}</span>
      </div>
    `;
  }

  private renderMarkdown(markdownArg: string, maxLinesArg = 500): TemplateResult[] {
    const lines = markdownArg.split('\n').slice(0, maxLinesArg);
    const blocks: TemplateResult[] = [];
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];
      const trimmed = line.trim();
      if (!trimmed) {
        index++;
        continue;
      }

      if (trimmed.startsWith('```')) {
        const language = trimmed.slice(3).trim().split(/\s+/)[0] ?? '';
        const codeLines: string[] = [];
        index++;
        while (index < lines.length && !lines[index].trim().startsWith('```')) {
          codeLines.push(lines[index]);
          index++;
        }
        index++;
        blocks.push(html`<pre><code>${this.renderHighlightedBlock(codeLines, language)}</code></pre>`);
        continue;
      }

      if (trimmed.startsWith('|') && lines[index + 1]?.trim().startsWith('|')) {
        const header = trimmed.split('|').slice(1, -1).map((cell) => cell.trim());
        index += 2;
        const rows: string[][] = [];
        while (index < lines.length && lines[index].trim().startsWith('|')) {
          rows.push(lines[index].trim().split('|').slice(1, -1).map((cell) => cell.trim()));
          index++;
        }
        blocks.push(html`
          <table>
            <thead><tr>${header.map((cell) => html`<th>${this.renderInlineMarkdown(cell)}</th>`)}</tr></thead>
            <tbody>${rows.map((row) => html`<tr>${row.map((cell) => html`<td>${this.renderInlineMarkdown(cell)}</td>`)}</tr>`)}</tbody>
          </table>
        `);
        continue;
      }

      if (trimmed.startsWith('>')) {
        const quoteLines: string[] = [];
        while (index < lines.length && lines[index].trim().startsWith('>')) {
          quoteLines.push(lines[index].trim().replace(/^>\s?/, ''));
          index++;
        }
        blocks.push(html`<blockquote>${this.renderInlineMarkdown(quoteLines.join(' '))}</blockquote>`);
        continue;
      }

      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const items: string[] = [];
        while (index < lines.length && /^[-*]\s/.test(lines[index].trim())) {
          items.push(lines[index].trim().slice(2));
          index++;
        }
        blocks.push(html`<ul>${items.map((item) => html`<li>${this.renderInlineMarkdown(item)}</li>`)}</ul>`);
        continue;
      }

      if (/^\d+\.\s/.test(trimmed)) {
        const items: string[] = [];
        while (index < lines.length && /^\d+\.\s/.test(lines[index].trim())) {
          items.push(lines[index].trim().replace(/^\d+\.\s/, ''));
          index++;
        }
        blocks.push(html`<ol>${items.map((item) => html`<li>${this.renderInlineMarkdown(item)}</li>`)}</ol>`);
        continue;
      }

      if (/^---+\s*$/.test(trimmed)) {
        blocks.push(html`<hr />`);
        index++;
        continue;
      }

      if (trimmed.startsWith('### ')) {
        blocks.push(html`<h3>${this.renderInlineMarkdown(trimmed.slice(4))}</h3>`);
        index++;
        continue;
      }
      if (trimmed.startsWith('## ')) {
        blocks.push(html`<h2>${this.renderInlineMarkdown(trimmed.slice(3))}</h2>`);
        index++;
        continue;
      }
      if (trimmed.startsWith('# ')) {
        blocks.push(html`<h1>${this.renderInlineMarkdown(trimmed.slice(2))}</h1>`);
        index++;
        continue;
      }

      const paragraphLines: string[] = [];
      while (index < lines.length && lines[index].trim() && !/^(#{1,3}\s|```|>|[-*]\s|\d+\.\s|\||---)/.test(lines[index].trim())) {
        paragraphLines.push(lines[index].trim());
        index++;
      }
      blocks.push(html`<p>${this.renderInlineMarkdown(paragraphLines.join(' '))}</p>`);
    }

    if (markdownArg.split('\n').length > maxLinesArg) {
      blocks.push(html`<p class="muted">Markdown preview limited to first ${maxLinesArg} lines.</p>`);
    }

    return blocks;
  }

  private renderInlineMarkdown(textArg: string): TInlinePart[] {
    const parts: TInlinePart[] = [];
    const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|`([^`]+)`/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(textArg))) {
      if (match.index > lastIndex) {
        parts.push(textArg.slice(lastIndex, match.index));
      }
      if (match[1]) {
        parts.push(html`<strong>${match[1]}</strong>`);
      } else if (match[2] && match[3]) {
        parts.push(html`<a href=${match[3]} target="_blank" rel="noopener">${match[2]}</a>`);
      } else if (match[4]) {
        parts.push(html`<code>${match[4]}</code>`);
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < textArg.length) {
      parts.push(textArg.slice(lastIndex));
    }
    return parts;
  }

  private renderHighlightedBlock(linesArg: string[], languageArg = ''): TemplateResult[] {
    return linesArg.map((line, index) => html`${this.renderHighlightedCode(line, languageArg)}${index < linesArg.length - 1 ? '\n' : ''}`);
  }

  private renderHighlightedCode(lineArg: string, languageArg = ''): TInlinePart[] {
    const parts: TInlinePart[] = [];
    const isShell = /^(bash|sh|shell|zsh)$/i.test(languageArg);
    const tokenRegex = isShell
      ? /(#.*$|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b(?:case|do|done|elif|else|esac|fi|for|function|if|in|then|while)\b|\b\d+(?:\.\d+)?\b)/g
      : /(\/\/.*$|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b(?:async|await|break|case|catch|class|const|continue|default|else|export|extends|false|finally|for|from|function|if|implements|import|interface|let|new|null|private|protected|public|return|switch|throw|true|try|type|undefined|var|while)\b|\b\d+(?:\.\d+)?\b)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(lineArg))) {
      const token = match[0];
      if (match.index > lastIndex) {
        parts.push(lineArg.slice(lastIndex, match.index));
      }
      parts.push(html`<span class=${this.getTokenClass(token)}>${token}</span>`);
      lastIndex = match.index + token.length;
    }

    if (lastIndex < lineArg.length) {
      parts.push(lineArg.slice(lastIndex));
    }
    return parts;
  }

  private getTokenClass(tokenArg: string): string {
    if (tokenArg.startsWith('//') || tokenArg.startsWith('/*') || tokenArg.startsWith('#')) {
      return 'tok-comment';
    }
    if (/^["'`]/.test(tokenArg)) {
      return 'tok-string';
    }
    if (/^\d/.test(tokenArg)) {
      return 'tok-number';
    }
    return 'tok-keyword';
  }

  private renderAvatar(labelArg: string, avatarUrlArg = ''): TemplateResult {
    return html`
      <span class="avatar">
        ${this.getInitials(labelArg)}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private emitNavigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', {
      detail: { hash: hashArg },
      bubbles: true,
      composed: true,
    }));
  }

  private emitCopy(textArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-copy', {
      detail: { text: textArg },
      bubbles: true,
      composed: true,
    }));
  }

  private getRepoHash(orgArg: string, repoArg: string, pathArg = ''): string {
    const base = `#/repo/${encodeURIComponent(orgArg)}/${encodeURIComponent(repoArg)}`;
    return pathArg ? `${base}/src/${this.encodePath(pathArg)}` : base;
  }

  private encodePath(pathArg: string): string {
    return pathArg.split('/').filter(Boolean).map((part) => encodeURIComponent(part)).join('/');
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${16}></dees-icon>`;
  }

  private isMarkdownFile(fileNameArg: string): boolean {
    return /(^|\.)mdx?$/i.test(fileNameArg);
  }

  private getFileLanguageLabel(fileNameArg: string): string {
    const lowerName = fileNameArg.toLowerCase();
    if (lowerName.endsWith('.md') || lowerName.endsWith('.mdx')) return 'Markdown';
    if (lowerName.endsWith('.ts') || lowerName.endsWith('.tsx')) return 'TypeScript';
    if (lowerName.endsWith('.js') || lowerName.endsWith('.jsx') || lowerName.endsWith('.mjs')) return 'JavaScript';
    if (lowerName.endsWith('.json')) return 'JSON';
    if (lowerName.endsWith('.html')) return 'HTML';
    if (lowerName.endsWith('.css')) return 'CSS';
    if (lowerName.endsWith('.rs')) return 'Rust';
    if (lowerName.endsWith('.sh')) return 'Shell';
    if (lowerName === 'dockerfile' || lowerName.endsWith('/dockerfile')) return 'Dockerfile';
    return 'Text';
  }


  private getInitials(labelArg: string): string {
    const parts = labelArg.split(/[.\s/-]+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'FG';
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    const months = Math.round(days / 30);
    return `${months}mo ago`;
  }

  private formatBytes(bytesArg: number): string {
    if (bytesArg < 1024) {
      return `${bytesArg} B`;
    }
    if (bytesArg < 1024 * 1024) {
      return `${Math.round(bytesArg / 1024)} KB`;
    }
    return `${(bytesArg / (1024 * 1024)).toFixed(1)} MB`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-file-view': DotglobalFileView;
  }
}
